import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import { tendersService } from '../../services/partnerService';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

const STATUS_OPTIONS = ['open', 'submitted', 'under_evaluation', 'won', 'lost'];
const STATUS_LABELS = { open: 'Open', submitted: 'Submitted', under_evaluation: 'Under Evaluation', won: 'Won', lost: 'Lost' };

const STATUS_COLORS = {
  open: 'bg-primary/10 text-primary',
  submitted: 'bg-accent/10 text-accent',
  'under evaluation': 'bg-warning/10 text-warning',
  won: 'bg-success/10 text-success',
  lost: 'bg-error/10 text-error',
};

const TenderProfilePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [tender, setTender] = useState(location?.state?.tender || null);
  const [loading, setLoading] = useState(!location?.state?.tender);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    value: '',
    tender_status: 'open',
    deadline: '',
    sector: '',
    description: '',
  });

  useEffect(() => {
    if (tender) {
      setForm({
        name: tender?.name || '',
        value: tender?.rawValue || '',
        tender_status: tender?.rawStatus || 'open',
        deadline: tender?.rawDeadline || '',
        sector: tender?.sector || '',
        description: tender?.description || '',
      });
    }
  }, [tender]);

  useEffect(() => {
    if (!tender && id) {
      const fetchTender = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            ?.from('tenders')
            ?.select('*, partners(brand_name, legal_name)')
            ?.eq('id', id)
            ?.single();
          if (error) throw error;
          const t = data;
          setTender({
            id: t?.id,
            name: t?.name,
            client: t?.partners?.brand_name || 'TBD',
            value: t?.value >= 1000000 ? `OMR ${(t?.value / 1000000)?.toFixed(1)}M` : `OMR ${(t?.value / 1000)?.toFixed(0)}K`,
            rawValue: t?.value,
            status: STATUS_LABELS?.[t?.tender_status] || 'Open',
            rawStatus: t?.tender_status || 'open',
            deadline: t?.deadline ? new Date(t.deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
            rawDeadline: t?.deadline || '',
            daysLeft: t?.deadline ? Math.max(0, Math.ceil((new Date(t.deadline) - new Date()) / (1000 * 60 * 60 * 24))) : null,
            technicalRole: 'Lead',
            financialRole: 'Support',
            partner_id: t?.partner_id || '',
            sector: t?.sector || '',
            description: t?.description || '',
          });
        } catch (err) {
          toast?.error('Failed to load tender');
        } finally {
          setLoading(false);
        }
      };
      fetchTender();
    }
  }, [id]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Tender name is required.'); return; }
    setSaving(true);
    try {
      await tendersService?.update(id, {
        name: form?.name,
        value: parseFloat(form?.value) || 0,
        tender_status: form?.tender_status,
        deadline: form?.deadline || null,
        sector: form?.sector,
        description: form?.description,
      });
      toast?.success('Tender updated successfully');
      setTender(prev => ({
        ...prev,
        name: form?.name,
        rawStatus: form?.tender_status,
        status: STATUS_LABELS?.[form?.tender_status] || 'Open',
        rawDeadline: form?.deadline,
        deadline: form?.deadline ? new Date(form.deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        sector: form?.sector,
        description: form?.description,
        rawValue: parseFloat(form?.value) || 0,
        value: parseFloat(form?.value) >= 1000000 ? `OMR ${(parseFloat(form?.value) / 1000000)?.toFixed(1)}M` : `OMR ${(parseFloat(form?.value) / 1000)?.toFixed(0)}K`,
      }));
      setIsEditing(false);
    } catch (err) {
      toast?.error(err?.message || 'Failed to update tender');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('tenders')?.delete()?.eq('id', id);
      if (error) throw error;
      toast?.success('Tender deleted');
      navigate('/engagement-management-hub');
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete tender');
      setDeleting(false);
    }
  };

  const statusKey = tender?.status?.toLowerCase();
  const statusClass = STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600';

  const getUrgencyColor = (daysLeft) => {
    if (daysLeft === null) return 'text-slate-500';
    if (daysLeft <= 3) return 'text-error font-semibold';
    if (daysLeft <= 7) return 'text-warning font-semibold';
    return 'text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <div className="flex items-center justify-center py-64">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{tender?.name || 'Tender'} — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <main className="px-24 lg:px-32 pt-24 pb-48">
          <div className="max-w-4xl mx-auto">
            {/* Back */}
            <button
              onClick={() => navigate('/engagement-management-hub')}
              className="flex items-center gap-8 text-sm text-slate-500 hover:text-slate-800 mb-24 transition-all"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Engagement Hub
            </button>

            {/* Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28 mb-20">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-16 mb-20">
                <div className="flex items-center gap-16">
                  <div className="p-14 bg-gradient-to-br from-warning to-accent rounded-xl shadow-md">
                    <Icon name="FileText" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">{tender?.name}</h1>
                    <p className="text-sm text-slate-500 mt-4">Client: {tender?.client}</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  {!isEditing && (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all"
                      >
                        <Icon name="Pencil" size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-all"
                      >
                        <Icon name="Trash2" size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <button onClick={() => setIsEditing(false)} className="px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">Cancel</button>
                      <button onClick={handleSave} disabled={saving} className="flex items-center gap-6 px-14 py-8 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-all">
                        {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Check" size={14} />}
                        Save Changes
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-8">
                <span className={`inline-flex items-center px-10 py-5 rounded-full text-xs font-semibold ${statusClass}`}>{tender?.status}</span>
                {tender?.daysLeft !== null && (
                  <span className={`inline-flex items-center gap-4 px-10 py-5 rounded-full text-xs bg-slate-100 ${getUrgencyColor(tender?.daysLeft)}`}>
                    <Icon name="Clock" size={12} />
                    {tender?.daysLeft} days left
                  </span>
                )}
              </div>
            </div>

            {/* Delete Confirm */}
            {showDeleteConfirm && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-24 mb-20 flex flex-col sm:flex-row sm:items-center gap-16">
                <div className="flex items-center gap-12 flex-1">
                  <div className="p-10 bg-red-100 rounded-full">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Delete this tender?</p>
                    <p className="text-xs text-red-600 mt-2">This action cannot be undone.</p>
                  </div>
                </div>
                <div className="flex gap-8">
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-16 py-8 text-sm border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-all">Cancel</button>
                  <button onClick={handleDelete} disabled={deleting} className="px-16 py-8 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition-all">
                    {deleting ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                <h2 className="text-base font-semibold text-slate-800 mb-20 flex items-center gap-8">
                  <Icon name="Info" size={16} className="text-primary" />
                  Tender Details
                </h2>
                {isEditing ? (
                  <div className="space-y-16">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Tender Name <span className="text-error">*</span></label>
                      <input name="name" value={form?.name} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Status</label>
                        <select name="tender_status" value={form?.tender_status} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          {STATUS_OPTIONS?.map(s => <option key={s} value={s}>{STATUS_LABELS?.[s]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Value (OMR)</label>
                        <input name="value" type="number" value={form?.value} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Deadline</label>
                        <input name="deadline" type="date" value={form?.deadline} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Sector</label>
                        <input name="sector" value={form?.sector} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Description</label>
                      <textarea name="description" value={form?.description} onChange={handleChange} rows={4} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-16">
                    <div className="grid grid-cols-2 gap-16">
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Client</p>
                        <p className="text-sm font-medium text-slate-800">{tender?.client || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Tender Value</p>
                        <p className="text-sm font-semibold text-slate-800">{tender?.value || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Deadline</p>
                        <p className="text-sm font-medium text-slate-800">{tender?.deadline || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Sector</p>
                        <p className="text-sm font-medium text-slate-800">{tender?.sector || '—'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-16 p-16 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Technical Role</p>
                        <p className="text-sm font-medium text-slate-800">{tender?.technicalRole || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Financial Role</p>
                        <p className="text-sm font-medium text-slate-800">{tender?.financialRole || '—'}</p>
                      </div>
                    </div>
                    {tender?.description && (
                      <div className="pt-16 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-8">Description</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{tender?.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-16">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="Clock" size={16} className="text-warning" />
                    Deadline
                  </h2>
                  <p className={`text-2xl font-bold mb-4 ${getUrgencyColor(tender?.daysLeft)}`}>
                    {tender?.daysLeft !== null ? `${tender?.daysLeft} days` : '—'}
                  </p>
                  <p className="text-xs text-slate-500">{tender?.deadline}</p>
                  {tender?.daysLeft !== null && tender?.daysLeft <= 7 && (
                    <div className="mt-12 flex items-center gap-6 text-xs text-warning font-medium">
                      <Icon name="AlertTriangle" size={12} />
                      Urgent — deadline approaching
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="Activity" size={16} className="text-primary" />
                    Quick Stats
                  </h2>
                  <div className="space-y-12">
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Status</span>
                      <span className={`text-xs font-semibold px-8 py-4 rounded-full ${statusClass}`}>{tender?.status}</span>
                    </div>
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Value</span>
                      <span className="text-xs font-semibold text-slate-800">{tender?.value}</span>
                    </div>
                    <div className="flex items-center justify-between py-8">
                      <span className="text-xs text-slate-500">Client</span>
                      <span className="text-xs font-medium text-slate-700 truncate max-w-[100px]">{tender?.client}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TenderProfilePage;
